import { useCallback, useEffect, useState } from 'react';
import { storage } from '@hypneuronerd/storage';
import {
  clearAgeCookies,
  getAgeCookie,
  getDevBypassCookie,
  setAgeCookie,
} from './cookie';

export const AGE_KEY = 'age-confirmed';
export const DEV_BYPASS_KEY = 'dev-bypass-age';
export const SHELL_ORIGIN = 'https://hypneuronerd.ing';

/**
 * Read the confirmation flag.
 *
 * Cross-subdomain cookie is the source of truth (so a single confirmation
 * on hypneuronerd.ing carries across every *.hypneuronerd.ing). localStorage
 * is checked as a fallback for migration — any user who confirmed under
 * the earlier localStorage-only implementation is treated as confirmed
 * and is silently migrated to the cookie on first read.
 */
function readConfirmed(): boolean {
  if (getDevBypassCookie()) return true;
  if (storage.get<boolean>(DEV_BYPASS_KEY, false)) return true;
  if (getAgeCookie()) return true;
  // Migration: legacy localStorage flag → cookie
  if (storage.get<boolean>(AGE_KEY, false)) {
    setAgeCookie(true);
    return true;
  }
  return false;
}

/**
 * Reactive read/write of the global age-confirmation flag.
 *
 * The shell calls `confirm()` from its full-screen overlay. Once set, the
 * flag is stored as a cookie scoped to `.hypneuronerd.ing`, so every
 * subdomain (and the apex) sees the same value without re-prompting.
 *
 * Sub-apps mount their own AgeGateOverlay; if the cookie is present from
 * the shell or another app, the overlay does not render.
 */
export function useAgeGate(): {
  confirmed: boolean;
  confirm: () => void;
  reset: () => void;
} {
  const [confirmed, setConfirmed] = useState<boolean>(() => readConfirmed());

  // Cross-tab sync via the `storage` event (localStorage). Cookies don't fire
  // events, but localStorage does — and the dev-bypass key is still
  // localStorage-only, so this catches that path. Also re-syncs on page focus
  // in case the cookie was set on another subdomain in another tab.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (!event.key) {
        setConfirmed(readConfirmed());
        return;
      }
      if (event.key.endsWith(AGE_KEY) || event.key.endsWith(DEV_BYPASS_KEY)) {
        setConfirmed(readConfirmed());
      }
    }
    function onFocus() {
      setConfirmed(readConfirmed());
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const confirm = useCallback(() => {
    setAgeCookie(true);
    // Belt-and-suspenders: also mirror to localStorage for legacy reads
    // and for parity with the previous implementation.
    storage.set(AGE_KEY, true);
    setConfirmed(true);
  }, []);

  const reset = useCallback(() => {
    clearAgeCookies();
    storage.remove(AGE_KEY);
    storage.remove(DEV_BYPASS_KEY);
    setConfirmed(false);
  }, []);

  return { confirmed, confirm, reset };
}
